import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { DataTable } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { FormModal } from "@/components/shared/FormModal";
import { Button } from "@/components/ui/button";
import { Boxes, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Items() {
  const { has } = useAtlas();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const canEdit = has("inventory.items.edit");

  const load = () => api.get("/inventory/items").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (v) => { await api.post("/inventory/items", { ...v, unit_cost: Number(v.unit_cost) || 0 }); toast.success("Item created"); load(); };

  const columns = [
    { key: "name", header: "Item", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "sku", header: "SKU", mono: true },
    { key: "vendor_name", header: "Vendor", render: (r) => r.vendor_name || <span className="text-slate-300">—</span> },
    { key: "unit_cost", header: "Unit cost", align: "right", mono: true, render: (r) => `$${(r.unit_cost || 0).toFixed(2)}` },
    { key: "total_quantity", header: "On hand", align: "right", mono: true, render: (r) => <span className="font-semibold">{r.total_quantity}</span> },
  ];

  return (
    <div>
      <PageHeader title="Items" subtitle="Vendors are shared Atlas Organizations with a vendor relationship">
        {canEdit && <Button onClick={() => setOpen(true)} data-testid="item-create-button"><Plus className="w-4 h-4 mr-1" /> New Item</Button>}
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState icon={Boxes} title="No items yet" description="Create an item and assign it a SKU to start tracking inventory." actionLabel={canEdit ? "New Item" : undefined} onAction={() => setOpen(true)} />
      ) : (
        <DataTable testid="inventory-items-table" rowTestId="item-row" columns={columns} rows={items} onRowClick={(r) => navigate(`/inventory/items/${r.id}`)} />
      )}

      <FormModal open={open} onOpenChange={setOpen} title="New Item" testid="item-form-modal" onSubmit={create}
        fields={[
          { name: "name", label: "Item name", required: true },
          { name: "sku", label: "SKU", required: true },
          { name: "description", label: "Description", type: "textarea" },
          { name: "unit", label: "Unit", default: "unit" },
          { name: "unit_cost", label: "Unit cost ($)", type: "number", default: 0 },
          { name: "vendor_org_id", label: "Vendor (Organization)", type: "select", options: orgs.map((o) => ({ value: o.id, label: o.name })) },
        ]} />
    </div>
  );
}
